import {
  Claim,
  Service,
  ServiceClaim,
  ServiceClaimOption,
  ServiceSummary,
  SortableColumns,
  SortOrder,
  User,
} from "./interfaces";

export function sortUsersBy(
  sortBy: keyof SortableColumns,
  order: SortOrder,
  users: User[],
  availableServices: Service[] = [],
): User[] {
  return users.sort((x, y) => {
    const current = getUserSortComparisonValue(x, sortBy, availableServices);
    const next = getUserSortComparisonValue(y, sortBy, availableServices);
    if (current < next) {
      return order === "asc" ? -1 : 1;
    }
    if (current > next) {
      return order === "asc" ? 1 : -1;
    }
    return 0;
  });
}

function getUserSortComparisonValue(
  user: User,
  sortBy: keyof SortableColumns,
  availableServices: Service[],
) {
  if (availableServices.map((s) => s.serviceReference).includes(sortBy)) {
    return user.services.map((s) => s.serviceReference).includes(sortBy);
  }
  return user[sortBy as keyof User] ?? "";
}

export function getFormattedDate(dateString: string): string {
  const date = new Date(dateString);
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
}

export function getNumberOfDaysBetween(startDate: Date, endDate: Date): number {
  const oneDay = 1000 * 60 * 60 * 24;
  const timeBetween = startDate.getTime() - endDate.getTime();

  return Math.round(timeBetween / oneDay);
}

export function getClaimDescription(
  claim: ServiceClaim,
  option: ServiceClaimOption,
): string {
  if (
    claim.options.length === 1 &&
    !claim.displaySingleServiceClaimOptionDescription
  ) {
    return claim.shortName;
  } else if (claim.displaySingleServiceClaimOptionDescription) {
    return option.claimValueDescription;
  }

  return `${claim.shortName} (${option.claimValueDescription})`;
}

export function isShowDropdown(service: ServiceSummary): boolean {
  return (
    service.displayDropdownForMultipleOptions &&
    service.serviceClaims.some((claim) => claim.options.length > 1)
  );
}

export function hasClaimType(claims: Claim[], claimType: string): boolean {
  return claims.find((sc) => sc.type === claimType) !== undefined;
}

export function hasClaimTypeAndValue(
  claims: Claim[],
  claimType: string,
  claimValue: string,
): boolean {
  const claim = claims.find((sc) => sc.type === claimType);
  return claim ? claim.value.includes(claimValue) : false;
}

export function getOptionsArray(service: ServiceSummary): ServiceClaimOption[] {
  const longestOptionsArray = service.serviceClaims
    .map((c) => c.options)
    .reduce((a, b) => (a.length > b.length ? a : b));
  return longestOptionsArray.sort((item1, item2) =>
    item1.claimValueDescription > item2.claimValueDescription ? 1 : -1,
  );
}

export function isContainSameClaimOptionValues(
  service: ServiceSummary,
): boolean {
  const allOptionValuesArrays = service.serviceClaims.map((c) =>
    c.options.map((o) => o.claimValue),
  );
  const longestOptionValuesArray = allOptionValuesArrays.reduce((a, b) =>
    a.length > b.length ? a : b,
  );
  const isContainSameValues = allOptionValuesArrays.map(
    (a) =>
      longestOptionValuesArray.length === a.length &&
      a.every((o) => longestOptionValuesArray.includes(o)),
  );
  return isContainSameValues.every((b) => b === true);
}

export function getExpiryDate(service: ServiceSummary): string {
  return service.nextLicenseExpiresOn !== undefined &&
    service.nextLicenseExpiresOn !== null
    ? ` (${getFormattedDate(service.nextLicenseExpiresOn)})`
    : "";
}

export function isValidAvailableSeats(
  service: ServiceSummary,
  selectedUsersCount: number,
): boolean {
  return (
    service.unlimitedSeats ||
    service.seatsTotal - service.seatsUsed >= selectedUsersCount
  );
}

export function equalsIgnoringCase(text1: string, text2: string) {
  return text1.localeCompare(text2, undefined, { sensitivity: "accent" }) === 0;
}
